import { getCurrentInstance, hasInjectionContext, inject, ref, type Ref } from 'vue';
import { useInternational } from '@/common/locale';
import { showErrorMsg, showWarningMsg } from '@/common/utils/commonUtils';
import { defineStore, storeToRefs } from 'pinia';
import {
  getUniqueId,
  useSlideDetail,
  type AddTabPanelInfo,
  type SlideTabPanelInfo,
} from '@/common/stores/slide-detail.logic';

export * from './slide-detail.logic';

let _popupTarget: {
  panel: Ref<SlideTabPanelInfo | undefined>;
  ctx: any;
  t: (key: string) => string;
} | null = null;

const isPopupDuplicate = (info: AddTabPanelInfo): boolean => {
  const pp = _popupTarget?.panel.value;
  if (!pp) return false;
  return pp.type === info.type && getUniqueId(pp) === getUniqueId(info);
};

const showPopupDuplicateWarning = () => {
  if (_popupTarget) {
    showWarningMsg(_popupTarget.ctx, _popupTarget.t('MESSAGE.POPUP_DUPLICATE_TARGET'));
  }
};

const useUtil = () => {
  const ctx = getCurrentInstance()!.appContext.config.globalProperties;
  const { t } = useInternational();

  const withErrorMsg =
    <T extends (...args: any[]) => any>(fn: T) =>
    (...args: Parameters<T>): ReturnType<T> | undefined => {
      try {
        return fn(...args);
      } catch (e: unknown) {
        if (e instanceof Error) {
          showErrorMsg(ctx, t(e.message));
        }
      }
    };

  return {
    withErrorMsg,
  };
};

export const useSlideDetailStore = defineStore('slideDetail', () => {
  const { addTabPanel: rawAddTabPanel, addSplitTabPanel, ...rest } = useSlideDetail();
  const { withErrorMsg } = useUtil();

  const isExpand = ref(false);

  const setIsExpand = (value: boolean) => {
    isExpand.value = value;
  };

  const addTabPanel = (
    ...args: Parameters<typeof rawAddTabPanel>
  ): ReturnType<typeof rawAddTabPanel> => {
    if (isPopupDuplicate(args[0])) {
      showPopupDuplicateWarning();
      return 'toggled_close';
    }
    return withErrorMsg(rawAddTabPanel)(...args) ?? 'unknown_error';
  };

  return {
    addTabPanel,
    addSplitTabPanel: withErrorMsg(addSplitTabPanel),
    isExpand,
    setIsExpand,
    ...rest,
  };
});

export const useSplitSlideDetailStore = defineStore('splitSlideDetail', () => {
  const slideDetailStore = useSlideDetailStore();
  const {
    addTabPanel,
    setSplitOpened,
    addSplitTabPanel,
    initSplitTabPanel,
    setIsSplitDetailLoading,
    selectedSplitTabComputed,
    setIsExpand,
    setSplitSlideDetailSubTitle,
  } = slideDetailStore;

  const {
    isSplitOpened,
    splitDetailComp,
    slideSplitTabPanel,
    splitSelectedSlideTabPanel,
    isSplitDetailLoading,
  } = storeToRefs(slideDetailStore);

  return {
    addTabPanel,
    isOpened: isSplitOpened,
    selectedTabComputed: selectedSplitTabComputed,
    deleteLastSlideDetailHistory: initSplitTabPanel,
    setOpened: setSplitOpened,
    addSplitTabPanel,
    detailComp: splitDetailComp,
    slideTabPanel: slideSplitTabPanel,
    selectedSlideTabPanel: splitSelectedSlideTabPanel,
    isDetailLoading: isSplitDetailLoading,
    initSplitTabPanel,
    setIsDetailLoading: setIsSplitDetailLoading,
    setIsExpand,
    setSlideDetailSubTitle: setSplitSlideDetailSubTitle,
  };
});

export const usePopupSlideDetailStore = defineStore('popupSlideDetail', () => {
  const mainStore = useSlideDetailStore();
  const {
    addTabPanel: _localAddTabPanel,
    addSplitTabPanel: _localAddSplitTabPanel,
    ...rest
  } = useSlideDetail();
  const ctx = getCurrentInstance()!.appContext.config.globalProperties;
  const { t } = useInternational();

  _popupTarget = { panel: rest.selectedSlideTabPanel, ctx, t };

  const isExpand = ref(false);

  const setIsExpand = (value: boolean) => {
    isExpand.value = value;
  };

  const addTabPanel = (
    ...args: Parameters<typeof mainStore.addTabPanel>
  ): ReturnType<typeof mainStore.addTabPanel> => {
    if (isPopupDuplicate(args[0])) {
      showPopupDuplicateWarning();
      return 'toggled_close';
    }
    return mainStore.addTabPanel(...args);
  };

  return {
    addTabPanel,
    addSplitTabPanel: mainStore.addTabPanel,
    isExpand,
    setIsExpand,
    ...rest,
  };
});

export const getSlideDetailStore = () => {
  if (!hasInjectionContext()) {
    return useSlideDetailStore();
  }
  const store = inject('slideDetailStore', useSlideDetailStore) || useSlideDetailStore;
  const resolvedStore = store() as ReturnType<typeof useSlideDetailStore> & {
    isDetailSlidePermissionDenied?: ReturnType<
      typeof useSlideDetailStore
    >['isDetailSlidePermissionDenied'];
  };

  // split detail store가 주입된 컨텍스트에서도 permission check API를 일관되게 제공한다.
  if (typeof resolvedStore.isDetailSlidePermissionDenied !== 'function') {
    resolvedStore.isDetailSlidePermissionDenied =
      useSlideDetailStore().isDetailSlidePermissionDenied;
  }

  return resolvedStore;
};

export const usePaSearchSectionStore = defineStore('paSearchSection', () => {
  const isShowSearchSection = ref<boolean>(false);

  const openSearchSection = () => {
    isShowSearchSection.value = true;
  };
  const resetOpenSearchSection = () => {
    isShowSearchSection.value = false;
  };

  return {
    isShowSearchSection,
    openSearchSection,
    resetOpenSearchSection,
  };
});
